import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import DtLogo from '../assets/img/App-Icon.svg?react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, NavLink, useHistory, useLocation, useParams } from 'react-router-dom'
import { ProcessStatusIcon } from './ProcessStatusIcon'
import { getHeaderTitle, Schema } from '../lib'
import { Header } from './Header'
import { useCore, useLogin } from '../hooks'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import { Order } from 'depoto-core'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faArrowAltCircleDown, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { faTruckLoading } from '@fortawesome/pro-regular-svg-icons'

type Props = {
  children: Element[] | ReactElement | any
  hasMainHeader: boolean
  isSettingsView?: boolean
}

// Menu item configuration type
type MenuItem = {
  path: string
  status?: string
  title: string
  icon: IconProp | string
  isIconComponent?: boolean
  isSeparator?: boolean
  badge?: number
  onClick?: () => void
  clearSearchParams?: boolean
}

export const SideMenu: React.FC<Props> = ({ children, hasMainHeader, isSettingsView }) => {
  const history = useHistory()
  const { isLoggedIn, handleLogout } = useLogin()
  const location = useLocation()
  const { pathname } = location
  const { core, currentOrder, setSearchString } = useCore()
  const [splitLocation, setSplitLocation] = useState<string[]>(pathname.split('/'))
  const [headerTitle, setHeaderTitle] = useState<string>(getHeaderTitle({ splitLocation, currentOrder }))
  const [isMenuExpanded, setIsMenuExpanded] = useState(false)
  // Initialize with 'received' as default (first menu item) to ensure something is always selected
  const [lastSelectedMainStatus, setLastSelectedMainStatus] = useState<string | null>('received')
  const serverType = core.services.oauth.session.clientType
  const desktopApp = window._depoto
  const routeParams = window.location.search

  const queryClient = useQueryClient()

  // Get current process status from URL for menu highlighting
  const currentProcessStatus = useMemo(() => {
    const pathSegments = pathname.split('/')
    if (pathSegments[1] === 'dashboard' && pathSegments[2]) {
      // Map URL process status to menu item status (handle special cases)
      const urlStatus = pathSegments[2]
      // Handle "received" vs "recieved" inconsistency
      if (urlStatus === 'recieved') return 'received'
      return urlStatus
    }
    if (pathSegments[1] === 'groups') return 'groups'
    if (pathSegments[1] === 'dispatch') return 'dispatch'
    if (pathSegments[1] === 'disposal') return 'disposal'
    return null
  }, [pathname])

  // Track last selected main menu status
  useEffect(() => {
    if (currentProcessStatus) {
      setLastSelectedMainStatus(currentProcessStatus)
    }
  }, [currentProcessStatus])

  // Check if any footer item matches the current path (settings is the only one with a path)
  const hasActiveFooterItem = useMemo(() => {
    return splitLocation[1] === 'settings'
  }, [splitLocation])

  const currentOrderId = useMemo(() => {
    const path = pathname.split('/')
    if (path[1] === 'order') {
      return path[path.length - 1]
    }
    return null
  }, [pathname])

  const isOrderLoading = useIsFetching({ queryKey: ['order', parseInt(currentOrderId)] })

  const orderQuery = useMemo(
    () => queryClient.getQueryData(['order', parseInt(currentOrderId)]) as Order,
    [currentOrderId, isOrderLoading],
  )
  const currentOrderProcessStatus = orderQuery?.processStatus?.id

  useEffect(() => {
    const splitLoc = pathname.split('/')
    setSplitLocation(splitLoc)
    setHeaderTitle(getHeaderTitle({ splitLocation: splitLoc, currentOrder }))
  }, [pathname, currentOrder])

  // Handler for menu expansion/collapse
  const handleMenuToggle = () => {
    setIsMenuExpanded(!isMenuExpanded)
  }

  // Handle clicks outside the menu to collapse it when expanded
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menuElement = document.getElementById('side-menu')
      if (isMenuExpanded && menuElement && !menuElement.contains(event.target as Node)) {
        setIsMenuExpanded(false)
      }
    }

    if (isMenuExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuExpanded])

  // Create menu configuration
  const mainMenuItems: MenuItem[] = [
    {
      path: `/dashboard/received`,
      status: 'received',
      title: 'Přijaté objednávky',
      icon: 'received',
      isIconComponent: true,
    },
    {
      path: `/dashboard/picking`,
      status: 'picking',
      title: 'Vyskladnění',
      icon: 'picking',
      isIconComponent: true,
    },
    {
      path: `/dashboard/packing`,
      status: 'packing',
      title: 'Balení',
      icon: 'packing',
      isIconComponent: true,
    },
    {
      path: `/dashboard/packed`,
      status: 'packed',
      title: 'Zabaleno',
      icon: 'packed',
      isIconComponent: true,
    },
    {
      path: `/dashboard/dispatched`,
      status: 'dispatched',
      title: 'Předáno dopravci',
      icon: 'dispatched',
      isIconComponent: true,
    },
    {
      path: `/dashboard/delivered`,
      status: 'delivered',
      title: 'Doručeno',
      icon: 'delivered',
      isIconComponent: true,
    },
    {
      path: `/dashboard/picking_error`,
      status: 'picking_error',
      title: 'Chyba vyskladnění',
      icon: 'picking_error',
      isIconComponent: true,
    },
    {
      isSeparator: true,
      path: '',
      title: '',
      icon: '',
    },
    {
      path: `/dashboard/history`,
      clearSearchParams: true,
      status: 'history',
      title: 'Historie',
      icon: ['far', 'clock-rotate-left'],
    },
    {
      path: `/groups`,
      status: 'groups',
      title: 'Skupiny',
      icon: ['far', 'boxes'],
    },
    {
      path: `/disposal`,
      status: 'disposal',
      title: 'Svoz',
      icon: faTruckLoading,
    },
  ]

  const footerMenuItems: MenuItem[] = [
    {
      path: '',
      title: 'Zavřít',
      icon: isMenuExpanded ? faChevronLeft : faChevronRight,
      onClick: () => setIsMenuExpanded(!isMenuExpanded),
    },
    {
      path: '/settings',
      title: 'Nastavení',
      icon: ['far', 'cog'],
    },

    {
      path: '',
      title: 'Odhlásit',
      icon: ['far', 'sign-out-alt'],
      onClick: () => handleLogout(() => history.push('/login')),
    },
  ]

  if (pathname === '/login' || pathname === '/external-login') {
    return children
  }

  const routeToClearSearchParams = pathname.includes('/dashboard/') ? 'groups' : 'dashboard'

  return (
    <div className="relative h-screen flex">
      {/* Background overlay when menu is expanded */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isMenuExpanded ? 'opacity-30 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuExpanded(false)}
      />

      {/* Sidebar */}
      <div
        id="side-menu"
        className={`fixed h-full z-30 flex flex-col gap-5 py-5 bg-base-blue text-primary-blue shadow-xl transition-all duration-300 ease-in-out ${
          isMenuExpanded ? 'w-64' : 'w-20'
        }`}
        onMouseLeave={() => setIsMenuExpanded(false)}>
        {/* Logo */}
        <Link to={'/dashboard'} className="w-full ml-6">
          <DtLogo width="35" />
        </Link>

        {/* Server type indicator */}
        <nav className="text-center" title={'Typ serveru'}>
          {serverType === 'prod' ? (
            <div></div>
          ) : serverType === 'stage' ? (
            <div className={'bg-danger text-white font-bold'}>[STAGE]</div>
          ) : serverType === 'dev' ? (
            <div className={'bg-danger text-white font-bold'}>[DEV]</div>
          ) : (
            ''
          )}
        </nav>

        {/* Main navigation */}
        <nav
          className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden"
          onMouseDown={e => {
            if (e.target === e.currentTarget) {
              setIsMenuExpanded(true)
            }
          }}>
          <nav className="flex flex-col gap-3 items-center menuItems mb-auto">
            <ul className={'w-full flex flex-col gap-3'}>
              {mainMenuItems.map((item, index) => {
                // Use last selected status as fallback if nothing matches
                const isLastSelected = lastSelectedMainStatus === item.status
                const isSelected = currentProcessStatus === item.status
                return item.isSeparator ? (
                  <div
                    key={`separator-${index}`}
                    className="mt-5 border-b border-light-blue w-14 ml-[.75rem] opacity-30"></div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={`${item.path}${item.clearSearchParams || item.path.includes(routeToClearSearchParams) ? '' : routeParams}`}
                    className={`${isSelected ? 'menuItem-active' : isLastSelected ? 'menuItem-active after:!bg-gray-400' : ''} h-14 flex flex-col justify-center w-full`}>
                    <li className={'flex items-center'} onClick={item.onClick}>
                      <span
                        className={`flex items-center cursor-pointer transition-colors ease-out duration-150 w-full`}>
                        <span
                          className={`flex justify-center transition-colors ease-out min-w-20 relative ${
                            isSelected ? '!text-primary-blue' : 'text-light-blue hover:text-primary-blue'
                          }`}>
                          {item.isIconComponent ? (
                            <ProcessStatusIcon status={item.icon as string} size="lg" />
                          ) : (
                            <FontAwesomeIcon icon={item.icon as IconProp} size={'lg'} />
                          )}
                          {item.badge !== undefined && (
                            <span className="absolute -top-5 right-1 bg-danger text-white text-sm font-semibold rounded-full w-6 h-6 flex items-center justify-center">
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          )}
                        </span>
                        {isMenuExpanded && (
                          <span className="font-medium whitespace-nowrap overflow-hidden text-gray-600 transition-colors ease-out">
                            {item.title}
                          </span>
                        )}
                      </span>
                    </li>
                  </NavLink>
                )
              })}
            </ul>
          </nav>

          {/* Footer navigation - settings, logout */}
          {isLoggedIn && (
            <nav className="flex flex-col gap-3 items-center menuItems">
              <ul className={'w-full flex flex-col gap-3'}>
                {footerMenuItems.map((item, index) => {
                  // Use last selected footer path as fallback if nothing matches current path
                  const matchesCurrentPath = splitLocation[1] === item.path?.split('/')[1]
                  // If no footer item matches current path, use last selected as fallback
                  const isActive = matchesCurrentPath
                  const isActuallyActive = matchesCurrentPath
                  const isActiveViaFallback = isActive && !isActuallyActive
                  return item.path ? (
                    <NavLink
                      key={`footer-${index}`}
                      to={item.path}
                      className={`${isActive ? (isActuallyActive ? 'menuItem-active' : 'menuItem-active after:!bg-gray-400') : ''} h-14 flex flex-col justify-center w-full`}>
                      <li className={'flex items-center'} onClick={item.onClick}>
                        <span
                          className={`flex items-center cursor-pointer transition-colors ease-out duration-150 w-full ${
                            isActuallyActive
                              ? 'text-primary-blue hover:text-primary-blue'
                              : isActiveViaFallback
                                ? 'text-gray-400 hover:text-gray-300'
                                : 'text-light-blue hover:text-primary-blue'
                          }`}>
                          <span
                            className={`flex justify-center transition-colors ease-out min-w-20 relative ${
                              isActiveViaFallback ? '!text-primary-blue' : ''
                            }`}>
                            <FontAwesomeIcon icon={item.icon as IconProp} size={'lg'} />
                            {item.badge !== undefined && (
                              <span className="absolute -top-1 -right-1 bg-danger text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                                {item.badge > 99 ? '99+' : item.badge}
                              </span>
                            )}
                          </span>
                          {isMenuExpanded && (
                            <span className="font-medium text-gray-600 whitespace-nowrap overflow-hidden transition-colors ease-out">
                              {item.title}
                            </span>
                          )}
                        </span>
                      </li>
                    </NavLink>
                  ) : (
                    <li key={`footer-${index}`} className={'flex items-center h-14 w-full'} onClick={item.onClick}>
                      <span
                        className={`flex items-center text-light-blue hover:text-danger cursor-pointer transition-colors ease-out duration-150 w-full`}>
                        <span className={`flex justify-center transition-colors ease-out min-w-20 relative`}>
                          <FontAwesomeIcon icon={item.icon as IconProp} size={'lg'} />
                        </span>
                        {isMenuExpanded && (
                          <span className="font-medium text-gray-600 whitespace-nowrap overflow-hidden transition-colors ease-out">
                            {item.title}
                          </span>
                        )}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </nav>
          )}
        </nav>
      </div>

      {/* Main content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ml-20 h-screen`}>
        <Header />
        <div className={'h-[calc(100vh-4rem)]'}>{children}</div>
      </div>
    </div>
  )
}
